import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSectionTree from './useSectionTree';
import useSectionActions from './useSectionActions';
import useSectionExport from './useSectionExport';
import useTreeDnd from './useTreeDnd';
import useTreeKeyboard from './useTreeKeyboard';
import useRowCommands from './useRowCommands';
import usePins from './usePins';
import SectionHeader from './SectionHeader';
import GroupHeader from './GroupHeader';
import PinnedSection from './PinnedSection';
import SectionEmpty from './SectionEmpty';
import SectionDialogs from './SectionDialogs';
import TreeRow from './TreeRow';

/**
 * One tree of the Documents sidebar, scoped to a single personal / device /
 * team bucket. The data lives in useSectionTree, the writes in
 * useSectionActions / useSectionExport / useTreeDnd / useRowCommands; this
 * file lays out the rows and wires the menus and dialogs (SectionDialogs).
 *
 * `variant`:
 * - 'single' (default since card 1.2) — the one tree under the scope
 *   switcher: Pinned (card 1.4), then "ALL DOCUMENTS" with the total.
 * - 'stacked' — the pre-redesign layout, one collapsible section per scope
 *   with its own "+" header; kept behind `documentsStackedSections()`.
 */
export default function TreeSection({
  sectionId,
  scope,
  title,
  variant = 'single',
  isTeam = false,
  eager = false,
  listenTitlebarEvents = false,
  emptyTitle,
  emptyHint,
  query,
  treeVersion,
  activeDocumentTabId,
  selected,
  setSelected,
  editing,
  setEditing,
  onRegisterCreate,
  treeRef,
  workspaces = [],
}) {
  const { t } = useTranslation();
  const single = variant === 'single';
  const tree = useSectionTree({ sectionId, scope, eager: eager || single, query, treeVersion });
  const actions = useSectionActions({ sectionId, scope, listenTitlebarEvents, tree, selected, setSelected, editing, setEditing });
  const exporting = useSectionExport({ scope, nodes: tree.nodes });
  const dnd = useTreeDnd({ sectionId, scope, tree, editingId: actions.editingId });
  const pins = usePins({ scope, nodes: tree.shownNodes, enabled: single });
  const commands = useRowCommands({ sectionId, scope, tree, actions, exporting, pins, setEditing });

  const ownTreeRef = useRef(null);
  const containerRef = treeRef || ownTreeRef;
  const [focusId, setFocusId] = useState(null);
  const [rowMenu, setRowMenu] = useState(null); // { x, y, node, keyboard, rowEl }
  const menuRanRef = useRef(false);
  const menuMenuOpenedAt = useRef(0);

  const { collapsed, searching, visibleIds, rootChildren, nodeById } = tree;
  const { handleNewDocument, handleNewFolder } = actions;

  // The header's New document / New folder act on this tree: inside the
  // selected folder when one is selected, else at the top.
  const selectedNode = actions.selectedId ? nodeById.get(actions.selectedId) : null;
  const createParent = selectedNode?.type === 'folder' ? selectedNode.id : null;
  const createRef = useRef(null);
  createRef.current = { newDocument: () => handleNewDocument(createParent), newFolder: () => handleNewFolder(createParent) };
  useEffect(() => {
    if (!onRegisterCreate) return undefined;
    onRegisterCreate(createRef);
    return () => onRegisterCreate(null);
  }, [onRegisterCreate]);

  const openRowMenu = (e, node, anchorEl = null) => {
    e.preventDefault();
    e.stopPropagation();
    setSelected({ sectionId, id: node.id });
    const rowEl = (anchorEl || e.currentTarget)?.closest?.('[role="treeitem"]') || null;
    menuRanRef.current = false;
    menuMenuOpenedAt.current = Date.now();
    if (anchorEl) {
      const r = anchorEl.getBoundingClientRect();
      const fromRow = anchorEl === rowEl;
      setRowMenu({ x: fromRow ? r.left + 24 : r.left, y: r.bottom + 4, node, keyboard: true, rowEl });
    } else {
      setRowMenu({ x: e.clientX, y: e.clientY, node, keyboard: false, rowEl });
    }
  };

  const closeRowMenu = (ran) => {
    if (ran) {
      menuRanRef.current = true;
      return;
    }
    const rowEl = rowMenu?.rowEl;
    const refocus = !menuRanRef.current;
    setRowMenu(null);
    menuRanRef.current = false;
    // Escape / outside click hands focus back to the row (card 3.3).
    if (refocus && rowEl) setTimeout(() => rowEl.isConnected && rowEl.focus({ preventScroll: true }), 0);
  };

  const onRowKeyDown = useTreeKeyboard({ sectionId, tree, dnd, openRowMenu, setSelected, setFocusId, containerRef });

  // The one tabbable row: the focused one, else the open page, else the first.
  const activeId = activeDocumentTabId?.startsWith('document-editor:') ? activeDocumentTabId.slice('document-editor:'.length) : null;
  const tabbableId =
    (focusId && nodeById.has(focusId) && focusId) ||
    (activeId && nodeById.has(activeId) && activeId) ||
    rootChildren[0]?.id ||
    null;

  const docCount = useMemo(() => tree.shownNodes.filter((n) => n.type === 'document').length, [tree.shownNodes]);

  const rowCtx = {
    sectionId,
    scope,
    searching,
    visibleIds,
    childrenByParent: tree.childrenByParent,
    expandedFolders: tree.expandedFolders,
    editingId: actions.editingId,
    selectedId: actions.selectedId,
    activeDocumentTabId,
    focusId: tabbableId,
    drop: dnd.drop,
    draggingId: dnd.draggingId,
    rowDragProps: dnd.rowDragProps,
    setSelected,
    setEditing,
    toggleFolder: tree.toggleFolder,
    handleRename: actions.handleRename,
    addInside: (node) => handleNewDocument(node.id),
    openRowMenu,
    onRowKeyDown,
  };

  const noMatches = searching && visibleIds && visibleIds.size === 0;
  const showBody = !collapsed || searching;

  const body = noMatches ? (
    <p className="doc-tree-note">{t('docSidebar.search.noMatches')}</p>
  ) : rootChildren.length === 0 ? (
    <SectionEmpty
      title={emptyTitle}
      hint={emptyHint}
      onNewDocument={() => handleNewDocument(null)}
      dropProps={dnd.rootDropProps}
      dropActive={dnd.rootDropActive}
    />
  ) : (
    <div
      ref={containerRef}
      role="tree"
      aria-label={title}
      className="doc-tree"
      onFocus={(e) => {
        const id = e.target?.getAttribute?.('data-node-id');
        if (id) setFocusId(id);
      }}
    >
      {rootChildren.map((node) => (
        <TreeRow key={node.id} node={node} depth={0} ctx={rowCtx} />
      ))}
      <div className={`doc-tree-root-drop${dnd.rootDropActive ? ' doc-tree-root-drop--active' : ''}`} {...dnd.rootDropProps} aria-hidden />
    </div>
  );

  return (
    <div className={`doc-section${single ? ' doc-section--single' : ''}`}>
      {single ? (
        <>
          {!searching && (
            <PinnedSection
              pins={pins}
              nodeById={nodeById}
              scope={scope}
              activeDocumentTabId={activeDocumentTabId}
              openRowMenu={openRowMenu}
            />
          )}
          <GroupHeader label={t('docSidebar.allDocuments')} count={docCount} collapsed={collapsed && !searching} onToggle={tree.toggleSection} />
        </>
      ) : (
        <SectionHeader
          title={title}
          isTeam={isTeam}
          collapsed={collapsed}
          onToggle={tree.toggleSection}
          openedAtRef={menuMenuOpenedAt}
          onNewDocument={() => handleNewDocument(null)}
          onNewFolder={() => handleNewFolder(null)}
        />
      )}

      {showBody && <div className="doc-section-body">{body}</div>}

      <SectionDialogs
        scope={scope}
        tree={tree}
        actions={actions}
        exporting={exporting}
        commands={commands}
        pins={pins}
        rowMenu={rowMenu}
        closeRowMenu={closeRowMenu}
        workspaces={workspaces}
      />
    </div>
  );
}
